import React, { useMemo } from "react";
import { Box, Flex, Link } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import {
  getBlogPostsForLanguage,
  getMarketingPageHeading,
  isMarketingBlogPost,
  MarketingContentIndexEntry,
} from "../../marketing-content/marketingContentLoader";
import { useLandingTheme } from "../landing-page/LandingThemeContext";
import { ThemedCaption, ThemedContainer, ThemedDisplayTitle } from "../landing-page/sections/themed/ThemedLayout";
import { ThemedClosingCta } from "../landing-page/sections/themed/ThemedClosingCta";
import { MarketingPageMain } from "./MarketingSiteShell";
import { MarketingMarkdown } from "./MarketingMarkdown";
import { getHeroImageResponsiveAttributes } from "../../marketing-content/marketingHeroImage";
import { formatMarketingPublishedDate, getMarketingSiteCopy } from "./marketingSiteCopy";
import { getFrontMatterString, MarketingTemplatePageProps } from "./marketingTemplatePageProps";

const HERO_IMAGE_FETCH_PRIORITY = { fetchpriority: "high" } as Record<string, string>;

const MORE_FROM_THE_BLOG_POST_COUNT = 3;

// "More from the blog": the three most recent other posts in the page's language.
const MoreFromTheBlogStrip = React.memo(function MoreFromTheBlogStrip({
  currentEntry,
}: {
  currentEntry: MarketingContentIndexEntry;
}) {
  const theme = useLandingTheme();
  const otherRecentPosts = useMemo(
    () =>
      getBlogPostsForLanguage(currentEntry.lang)
        .filter((post) => post.route !== currentEntry.route)
        .slice(0, MORE_FROM_THE_BLOG_POST_COUNT),
    [currentEntry]
  );
  if (otherRecentPosts.length === 0) return null;
  return (
    <Box as="section" borderTop={`1px solid ${theme.color.border}`} pt={{ base: "40px", md: "48px" }}>
      <ThemedContainer maxW="720px">
        <ThemedDisplayTitle level="sub">{getMarketingSiteCopy(currentEntry.lang).moreFromTheBlog}</ThemedDisplayTitle>
        <Flex direction="column" gap="18px" mt="24px">
          {otherRecentPosts.map((post) => (
            <Box key={post.route}>
              <Link
                as={RouterLink}
                to={post.route}
                fontFamily={theme.font.display}
                fontWeight={700}
                fontSize={{ base: "19px", md: "21px" }}
                lineHeight={1.3}
                color={theme.color.heading}
                _hover={{ color: theme.color.accent, textDecoration: "none" }}
              >
                {getMarketingPageHeading(post)}
              </Link>
              {post.publishedDate && (
                <ThemedCaption mt="4px">{formatMarketingPublishedDate(post.publishedDate, post.lang)}</ThemedCaption>
              )}
            </Box>
          ))}
        </Flex>
      </ThemedContainer>
    </Box>
  );
});

/**
 * The `article` template: a reading column with (for blog posts) the category / author / date row,
 * the heading, the hero image and the Markdown body, then a "more from the blog" strip for blog
 * posts and the closing call to action. The markup inside `<main>` follows the shared contract with
 * the build-time static HTML.
 */
export const MarketingArticlePage = React.memo(function MarketingArticlePage({
  entry,
  frontMatter,
  markdownBody,
  renderedBodyHtml,
}: MarketingTemplatePageProps) {
  const isBlogPost = isMarketingBlogPost(entry);
  const heading = getMarketingPageHeading(entry);
  const heroImageAlt = getFrontMatterString(frontMatter, "heroImageAlt") ?? heading;
  const metaParts = useMemo(() => {
    if (!isBlogPost) return [];
    const formattedPublishedDate = entry.publishedDate
      ? formatMarketingPublishedDate(entry.publishedDate, entry.lang)
      : null;
    return [entry.category, entry.author, formattedPublishedDate].filter(Boolean);
  }, [entry, isBlogPost]);

  return (
    <>
      <MarketingPageMain template="article">
        <article className="marketing-article">
          {metaParts.length > 0 && (
            <p className="marketing-article-meta">
              {metaParts.map((part, index) => (
                <React.Fragment key={index}>
                  {index > 0 && " · "}
                  <bdi>{part}</bdi>
                </React.Fragment>
              ))}
            </p>
          )}
          <h1 className="marketing-article-heading">{heading}</h1>
          {entry.heroImage && (
            <img
              className="marketing-article-hero"
              src={entry.heroImage}
              {...getHeroImageResponsiveAttributes(entry)}
              width={entry.heroImageSize?.width}
              height={entry.heroImageSize?.height}
              // The page's largest image: fetched ahead of the scripts (React 18 forwards only this spelling).
              {...HERO_IMAGE_FETCH_PRIORITY}
              alt={heroImageAlt}
            />
          )}
          <MarketingMarkdown
            className="marketing-article-body"
            markdown={markdownBody}
            renderedHtml={renderedBodyHtml}
          />
        </article>
        {isBlogPost && <MoreFromTheBlogStrip currentEntry={entry} />}
      </MarketingPageMain>
      <ThemedClosingCta />
    </>
  );
});
